import { x25519 } from "@noble/curves/ed25519.js";
import { xchacha20poly1305 } from "@noble/ciphers/chacha.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { assertKeyLength, concatBytes, generateNonce, utf8ToBytes, zeroBytes } from "./bytes.js";
import { CryptoError } from "./errors.js";
const SEAL_INFO = utf8ToBytes("passwordflow/seal-v1");
const SEAL_AAD = utf8ToBytes("passwordflow/seal-v1");
const INVITE_TOKEN_INFO = utf8ToBytes("passwordflow/invite-token-v1");
const INVITE_WRAP_INFO = utf8ToBytes("passwordflow/invite-wrap-v1");
const EMPTY_SALT = new Uint8Array(0);
const NONCE_BYTES = 24;
const TAG_BYTES = 16;
const PUBLIC_KEY_BYTES = 32;
export const IDENTITY_AAD = utf8ToBytes("passwordflow/identity-v1");
export const ORG_KEY_AAD = utf8ToBytes("passwordflow/orgkey-invite-v1");
export function generateIdentity() {
    const pair = x25519.keygen();
    return { publicKey: pair.publicKey, secretKey: pair.secretKey };
}
export function identityPublicKey(secretKey) {
    assertKeyLength(secretKey, 32, "IdentitySecretKey");
    return x25519.getPublicKey(secretKey);
}
export function generateOrgKey() {
    const out = new Uint8Array(32);
    crypto.getRandomValues(out);
    return out;
}
function assertNonce(nonce) {
    if (nonce.length !== NONCE_BYTES) {
        throw new CryptoError("invalid-nonce-length", "Nonce must be 24 bytes.");
    }
}
function deriveSealKey(shared, ephemeralPub, recipientPub) {
    return hkdf(sha256, shared, concatBytes(ephemeralPub, recipientPub), SEAL_INFO, 32);
}
/** Public-key seal: ephemeral X25519 key agreement, then XChaCha20-Poly1305. Output: ephPub(32) | nonce(24) | ciphertext. */
export function sealTo(recipientPub, plaintext) {
    const ephemeral = x25519.keygen();
    try {
        return sealToWith(recipientPub, plaintext, ephemeral.secretKey, generateNonce());
    }
    finally {
        zeroBytes(ephemeral.secretKey);
    }
}
/** Fixed ephemeral key and nonce, for the published test vectors only. */
export function sealToWith(recipientPub, plaintext, ephemeralSecret, nonce) {
    assertKeyLength(recipientPub, PUBLIC_KEY_BYTES, "RecipientPublicKey");
    assertKeyLength(ephemeralSecret, 32, "EphemeralSecretKey");
    assertNonce(nonce);
    const ephemeralPub = x25519.getPublicKey(ephemeralSecret);
    const shared = x25519.getSharedSecret(ephemeralSecret, recipientPub);
    const key = deriveSealKey(shared, ephemeralPub, recipientPub);
    const ciphertext = xchacha20poly1305(key, nonce, SEAL_AAD).encrypt(plaintext);
    zeroBytes(shared);
    zeroBytes(key);
    return concatBytes(concatBytes(ephemeralPub, nonce), ciphertext);
}
export function openSealed(identity, sealed) {
    if (sealed.length < PUBLIC_KEY_BYTES + NONCE_BYTES + TAG_BYTES) {
        throw new CryptoError("invalid-input", "Sealed payload is too short.");
    }
    const ephemeralPub = sealed.slice(0, PUBLIC_KEY_BYTES);
    const nonce = sealed.slice(PUBLIC_KEY_BYTES, PUBLIC_KEY_BYTES + NONCE_BYTES);
    const ciphertext = sealed.slice(PUBLIC_KEY_BYTES + NONCE_BYTES);
    try {
        const shared = x25519.getSharedSecret(identity.secretKey, ephemeralPub);
        const key = deriveSealKey(shared, ephemeralPub, identity.publicKey);
        const plain = xchacha20poly1305(key, nonce, SEAL_AAD).decrypt(ciphertext);
        zeroBytes(shared);
        zeroBytes(key);
        return plain;
    }
    catch {
        throw new CryptoError("decrypt-failed", "Decryption failed.");
    }
}
/** Symmetric seal under a 32-byte key. Output: nonce(24) | ciphertext. */
export function sealWithKey(key, plaintext, aad) {
    return sealWithKeyNonce(key, generateNonce(), plaintext, aad);
}
/** Fixed-nonce form for the published test vectors. A nonce must never be used twice with one key. */
export function sealWithKeyNonce(key, nonce, plaintext, aad) {
    assertKeyLength(key, 32, "SealKey");
    assertNonce(nonce);
    const ciphertext = xchacha20poly1305(key, nonce, aad).encrypt(plaintext);
    return concatBytes(nonce, ciphertext);
}
export function openWithKey(key, packed, aad) {
    assertKeyLength(key, 32, "SealKey");
    if (packed.length < NONCE_BYTES + TAG_BYTES) {
        throw new CryptoError("invalid-input", "Sealed payload is too short.");
    }
    try {
        return xchacha20poly1305(key, packed.slice(0, NONCE_BYTES), aad).decrypt(packed.slice(NONCE_BYTES));
    }
    catch {
        throw new CryptoError("decrypt-failed", "Decryption failed.");
    }
}
export function generateInviteSecret() {
    const out = new Uint8Array(32);
    crypto.getRandomValues(out);
    return out;
}
/** Server-visible lookup handle for an invite. Derived from the secret, so the server never sees the secret itself. */
export function inviteToken(secret) {
    assertKeyLength(secret, 32, "InviteSecret");
    return hkdf(sha256, secret, EMPTY_SALT, INVITE_TOKEN_INFO, 32);
}
/** Key that wraps the org key for the invite. Known only to someone holding the secret. */
export function inviteWrapKey(secret) {
    assertKeyLength(secret, 32, "InviteSecret");
    return hkdf(sha256, secret, EMPTY_SALT, INVITE_WRAP_INFO, 32);
}
//# sourceMappingURL=team.js.map